import React, { useState, useEffect } from "react";
import { Box, Typography, styled, keyframes } from "@mui/material";

interface CalculatingFitProps {
  matchNames: string[];
  onComplete: () => void;
}

const dotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.4); opacity: 1; }
`;

const matchReveal = keyframes`
  0%   { opacity: 0; transform: translateY(-4px); }
  15%  { opacity: 1; transform: translateY(0); }
  60%  { opacity: 1; transform: translateY(12px); }
  100% { opacity: 0; transform: translateY(48px); }
`;

const Container = styled(Box, {
  label: "CalculatingFitContainer",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  marginTop: 48,
}));

const DotsRow = styled(Box, {
  label: "CalculatingFitDotsRow",
})(() => ({
  display: "flex",
  gap: 10,
  alignItems: "center",
}));

const Dot = styled(Box, {
  label: "CalculatingFitDot",
  shouldForwardProp: (prop) => prop !== "dotColor" && prop !== "delay",
})<{ dotColor: string; delay: number }>(({ dotColor, delay }) => ({
  width: 14,
  height: 14,
  borderRadius: "50%",
  backgroundColor: dotColor,
  animation: `${dotPulse} 0.7s ${delay}s ease-in-out infinite`,
}));

const Label = styled(Typography, {
  label: "CalculatingFitLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#fff5cc",
  textAlign: "center",
}));

const MatchArea = styled(Box, {
  label: "CalculatingFitMatchArea",
})(() => ({
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const MatchItem = styled(Box, {
  label: "CalculatingFitMatchItem",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  animation: `${matchReveal} 0.85s ease-in-out forwards`,
}));

const MatchLabel = styled(Typography, {
  label: "CalculatingFitMatchLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#fff5cc",
}));

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3 8.5L6.5 12L13 5"
      stroke="#36b37e"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DOTS = [
  { color: "#c684ff", delay: 0 },
  { color: "#fa830d", delay: 0.175 },
  { color: "#00b8d9", delay: 0.35 },
  { color: "#36b37e", delay: 0.525 },
];

export const CalculatingFit = ({
  matchNames,
  onComplete,
}: CalculatingFitProps) => {
  const [visibleIndex, setVisibleIndex] = useState(-1);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    matchNames.forEach((_, i) => {
      timers.push(setTimeout(() => setVisibleIndex(i), (i + 1) * 2000));
    });

    const totalDuration = Math.max(matchNames.length * 2000 + 500, 2000);
    timers.push(setTimeout(onComplete, totalDuration));

    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      <DotsRow>
        {DOTS.map(({ color, delay }) => (
          <Dot key={color} dotColor={color} delay={delay} />
        ))}
      </DotsRow>
      <Label>Calculating your fit…</Label>
      <MatchArea>
        {visibleIndex >= 0 && visibleIndex < matchNames.length && (
          <MatchItem key={visibleIndex}>
            <CheckIcon />
            <MatchLabel>{matchNames[visibleIndex]}</MatchLabel>
          </MatchItem>
        )}
      </MatchArea>
    </Container>
  );
};
